import { request } from '@/utils/http'
import type { BusinessFileVO } from '@/api/universal-file'

export type SupplierBidTransportMode = 'FCL' | 'LCL' | 'AIR'

export interface SupplierBidContainer {
  id: string
  containerType: string
  quantity?: number
  hasPriceLimit?: boolean
  minPrice?: number
  maxPrice?: number
  currency?: string
}

export interface SupplierBidRoute {
  id: string
  transportMode: SupplierBidTransportMode
  sortOrder?: number
  routeCode?: string
  tenderScheme?: string
  originCountry?: string
  originPort?: string
  originCity?: string
  originCityId?: string | number
  originWarehouseId?: string | number
  /** 目的仓库基础资料ID。 */
  destinationWarehouseId?: string | number
  destinationCountry?: string
  destinationPort?: string
  destinationCity?: string
  destinationCityId?: string | number
  serviceType?: string
  transportClause?: string
  tradeClause?: string
  ddEnabled?: boolean
  weightKg?: number
  cbm?: number
  hasPriceLimit?: boolean
  minPrice?: number
  maxPrice?: number
  currency?: string
  remark?: string
  containers: SupplierBidContainer[]
}

export interface SupplierBidFeeItem {
  id: string
  feeId: string
  /** 全程费用的运输段；报价上下限按该段隔离，普通历史费用为空。 */
  transportSegment?: 'ORIGIN' | 'BASIC' | 'DESTINATION'
  transportMode: SupplierBidTransportMode
  feeCode?: string
  feeNameCn?: string
  feeNameEn?: string
  quoteVariant?: string
  feeCategory?: string
  basicFee?: boolean
  currency?: string
  billingUnit?: string
  required?: boolean
  /** 发布快照中的费用上下限填报开关。 */
  minMaxEnabled?: boolean
  sortOrder?: number
}

export interface SupplierBidCarrierOption {
  id: string
  /** 航司/船公司业务代码；外链应标页与英文名称组合展示。 */
  carrierCode?: string
  nameCn?: string
  nameEn?: string
  carrierType: 0 | 1
  enabled: boolean
}

export interface SupplierBidQuote {
  /** 按运输方式和费用ID保存收费金额上下限；空对象表示主动清空历史上下限。 */
  feeBounds?: Record<string, SupplierBidFeeBounds>
  /** 服务端根据标的物数量及上下限计算的费用，前端只读。 */
  calculatedFees?: Record<string, SupplierBidCalculatedFee>
  quoteId?: string
  /** 未完成草稿行所属运输方式，重新打开时用于恢复页签。 */
  pendingTransportMode?: SupplierBidTransportMode
  routeId: string
  /** 服务端按应标行实际港口生成的线路代码。 */
  routeCode?: string
  containerId?: string | null
  originPort?: string
  /** 招标未指定时供应商选填的起运城市名称。 */
  originCity?: string
  /** 招标未指定时供应商选填的起运仓库基础资料ID。 */
  originWarehouseId?: string | number
  destinationPort?: string
  /** 招标未指定时供应商选填的目的城市名称。 */
  destinationCity?: string
  /** 供应商补填或确认的目的仓库基础资料ID。 */
  destinationWarehouseId?: string | number
  participating: boolean
  routingType?: '直航' | '中转'
  transitPortIds: string[]
  /** 兼容旧版应标详情；新版保存使用 transitPortIds。 */
  transitPort?: string
  effectiveDate?: string
  expiryDate?: string
  transitCarrierIds: string[]
  vesselSchedule?: string
  flightSchedule?: string
  transitTimeDays?: number
  /** 港前段运输周期，单位天。 */
  prePortTransitDays?: number
  /** 尾程段运输周期，单位天。 */
  lastMileTransitDays?: number
  transitFreeDdDays?: number
  transitFreeStorageDays?: number
  /**
   * 兼容历史应标详情返回的目的港清关时间字段；保存请求统一使用destClearanceDays。
   */
  destinationClearanceDays?: number
  destClearanceDays?: number
  overallAchievementRate?: number
  transitFreeStorageHours?: number
  transitFreeCfsStorageHours?: number
  achievementRate?: number
  freeContainerType?: 'mixed' | 'separate'
  mixedDays?: number
  separateContainerDays?: number
  separateStorageDays?: number
  storageDays?: number
  bidRemark?: string
  feeQuotes: Record<string, number | undefined>
}

export interface SupplierBidFeeBounds {
  min?: number | null
  max?: number | null
}

export interface SupplierBidCalculatedFee {
  quantity?: number | null
  unitRate: number
  min?: number | null
  max?: number | null
  amount?: number | null
  quoteVariant: string
  includedInTotal: boolean
}

export interface SupplierBidAttachment {
  fileId: string
  fileName: string
  fileSize: number
  uploadTime?: string
}

export interface SupplierBidEvaluationParameter {
  parameterCode: string
  label: string
  value: number
  valueUnit?: string
  currency?: string
  sourceType: string
  sourceReference?: string
  description?: string
  sortOrder?: number
}

export interface SupplierBidEvaluationDetail {
  id: string
  parentId: string
  routeId: string
  containerId?: string
  quoteId: string
  transportMode: SupplierBidTransportMode
  evaluationScope: string
  evaluationScopeLabel?: string
  evaluationProject: string
  evaluationProjectLabel?: string
  description?: string
  score?: number
  formulaVersion: string
  sortOrder?: number
  parameters: SupplierBidEvaluationParameter[]
}

export interface SupplierBidDetail {
  tenderId: string
  /** 招标发起方租户ID，用于匿名应标页查询发起方基础资料。 */
  tenantId?: string | number
  tenderCode: string
  title: string
  tenderCompanyName?: string
  tenderType?: 'YEAR' | 'QUARTER' | 'MONTH'
  /** 货物类型基础资料ID。 */
  cargoType?: string | number
  /** 后端按货物类型基础资料ID解析出的展示名称。 */
  cargoTypeName?: string
  biddingMethod?: string
  /** 招标合同开始日期，作为应标报价生效日期的默认值。 */
  contractStartDate?: string
  /** 招标合同结束日期，作为应标报价截止日期的默认值。 */
  contractEndDate?: string
  tenderStatus: number
  currentRound: number
  totalRounds?: number
  roundStartTime?: string
  roundEndTime?: string
  requireDeposit?: boolean
  depositAmount?: number
  depositCurrency?: string
  depositDeadline?: string
  tenderRemark?: string
  tenderAttachments?: BusinessFileVO[]
  invitationStatus: string
  expiresAt?: string
  editable: boolean
  supplierId?: string | number
  supplierName: string
  contactName?: string
  contactEmail?: string
  bidId?: string
  bidCode?: string
  bidStatus: 'DRAFT' | 'SUBMITTED'
  bidVersion?: number
  /** 完整草稿快照需保留用户主动清空的字段。 */
  draftQuoteSnapshot?: boolean
  submittedAt?: string
  bidUpdatedAt?: string
  bidRemark?: string
  routes: SupplierBidRoute[]
  feeItems: SupplierBidFeeItem[]
  carriers: SupplierBidCarrierOption[]
  quotes: SupplierBidQuote[]
  evaluationDetails: SupplierBidEvaluationDetail[]
  attachments: SupplierBidAttachment[]
}

export interface SupplierBidSaveRequest {
  tenderId: string
  token: string
  version?: number
  contactName: string
  contactEmail: string
  bidRemark?: string
  quotes: SupplierBidQuote[]
  attachments: SupplierBidAttachment[]
}

export interface SupplierBidImportError {
  sheetName: string
  rowNumber: number
  columnName: string
  cellValue: string
  errorCode: string
  message: string
}

export interface SupplierBidImportRow {
  /** 实际出现的上下限列，用于区分缺列与主动清空。 */
  feeBoundFields?: Record<string, Array<'min' | 'max'>>
  sheetName: string
  rowNumber: number
  quote?: SupplierBidQuote
}

export interface SupplierBidImportPreview {
  totalRows: number
  rows: SupplierBidImportRow[]
  errors: SupplierBidImportError[]
}

/** 查询邮件令牌绑定企业的应标详情。 */
export function getSupplierBidDetail(tenderId: string, token: string) {
  return request.post<SupplierBidDetail>('/price/anonymous-access/bidding/supplierBid/detail', {
    tenderId,
    token
  })
}

/** 下载当前供应商令牌可见的招标附件。 */
export function downloadTenderAttachment(tenderId: string, token: string, documentId: string) {
  return request.get<Blob>('/price/anonymous-access/bidding/supplierBid/tenderAttachment', {
    tenderId,
    token,
    documentId
  }, {
    responseType: 'blob'
  })
}

/** 查询招标方开标后可见的只读结构化标书。 */
export function getInternalBidBookDetail(bidId: string, viewType: 'BID_VIEW' | 'CROSS_BID_VIEW' = 'BID_VIEW') {
  return request.post<SupplierBidDetail>('/price/bidding/biddingTender/bidBookDetail', {
    bidId,
    viewType
  })
}

/** 保存当前令牌绑定企业的本轮应标草稿。 */
export function saveSupplierBidDraft(params: SupplierBidSaveRequest) {
  return request.post<SupplierBidDetail>(
    '/price/anonymous-access/bidding/supplierBid/saveDraft',
    params
  )
}

/** 提交当前令牌绑定企业的本轮标书。 */
export function submitSupplierBid(params: SupplierBidSaveRequest) {
  return request.post<SupplierBidDetail>(
    '/price/anonymous-access/bidding/supplierBid/submit',
    params
  )
}

/**
 * 通扫供应商报价文件并返回无写入预览。
 *
 * @param file XLS、XLSX或CSV文件
 * @param tenderId 招标ID
 * @param token 邮件专属令牌
 * @param transportMode 当前报价页签运输方式
 * @returns 标准化报价行及全部行列错误
 */
export function previewSupplierBidImport(
  file: File,
  tenderId: string,
  token: string,
  transportMode: SupplierBidTransportMode
) {
  const formData = new FormData()
  formData.append('file', file)
  formData.append('tenderId', tenderId)
  formData.append('token', token)
  formData.append('transportMode', transportMode)
  return request.post<SupplierBidImportPreview>(
    '/price/anonymous-access/bidding/supplierBid/pricing/import-preview',
    formData
  )
}
